import { useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import {
  Button,
  DataTable,
  FilterBar,
  FilterField,
  Input,
  PageShell,
  Pagination,
  RadixSelect,
  SectionCard,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import {
  downloadInvoiceTemplate,
  useInvoiceBulkJobsQuery,
  useInvoiceBulkUploadMutation,
} from '../hooks/useInvoiceBulk';
import { triggerBlobDownload } from '../lib/download';
import { CourierReference } from '../components/CourierReference';
import { InvoiceBulkJobDetailDialog } from '../components/InvoiceBulkJobDetailDialog';
import {
  BULK_JOB_STATUS_LABELS,
  BULK_JOB_STATUSES,
  SUPPLIER_SCOPE_LABELS,
  SUPPLIER_SCOPES,
  type BulkJobStatus,
  type InvoiceBulkJob,
  type InvoiceBulkJobFilter,
  type SupplierScope,
} from '../types/invoiceBulk';

// 송장 등록 권한 = 출고 담당(FULFILLMENT) + 운영/슈퍼.
const INVOICE_WRITE_ROLES = ['FULFILLMENT_AGENT', 'OPS_ADMIN', 'SUPER_ADMIN'] as const;

const PAGE_SIZE = 20;

const EMPTY_FILTER: InvoiceBulkJobFilter = {
  supplierScope: '',
  keyword: '',
  status: '',
  dateFrom: '',
  dateTo: '',
};

const STATUS_VARIANT: Record<BulkJobStatus, 'warning' | 'success' | 'danger'> = {
  COMPLETED: 'success',
  PARTIAL: 'warning',
  FAILED: 'danger',
};

const supplierFilterOptions = [
  { value: '', label: '전체' },
  ...SUPPLIER_SCOPES.filter((s) => s !== 'ALL').map((s) => ({ value: s, label: SUPPLIER_SCOPE_LABELS[s] })),
];

const supplierUploadOptions = SUPPLIER_SCOPES.map((s) => ({ value: s, label: SUPPLIER_SCOPE_LABELS[s] }));

const statusFilterOptions = [
  { value: '', label: '전체' },
  ...BULK_JOB_STATUSES.map((s) => ({ value: s, label: BULK_JOB_STATUS_LABELS[s] })),
];

export const InvoiceBulkPage = () => {
  const canWrite = useHasRole(INVOICE_WRITE_ROLES);

  // 검색 폼 입력값(draft)과 적용된 필터(applied)를 분리 — "검색" 클릭 시 적용.
  const [draft, setDraft] = useState<InvoiceBulkFilterDraft>({
    supplierScope: '',
    keyword: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = useState<InvoiceBulkJobFilter>(EMPTY_FILTER);
  const [page, setPage] = useState(1);

  // 다운로드/업로드 컨트롤.
  const [uploadScope, setUploadScope] = useState<SupplierScope>('ALL');
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [detailJobId, setDetailJobId] = useState<string | null>(null);

  const jobsQuery = useInvoiceBulkJobsQuery(applied, page, PAGE_SIZE);
  const upload = useInvoiceBulkUploadMutation();

  const totalItems = jobsQuery.data?.pagination?.totalItems ?? jobsQuery.data?.items.length ?? 0;

  const applyFilter = () => {
    setApplied({
      supplierScope: draft.supplierScope,
      keyword: draft.keyword,
      status: draft.status,
      dateFrom: draft.dateFrom,
      dateTo: draft.dateTo,
    });
    setPage(1);
  };

  const resetFilter = () => {
    setDraft({ supplierScope: '', keyword: '', status: '', dateFrom: '', dateTo: '' });
    setApplied(EMPTY_FILTER);
    setPage(1);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await downloadInvoiceTemplate(uploadScope === 'ALL' ? '' : uploadScope);
      triggerBlobDownload(blob, filename);
      toast.success('배송대기 주문 템플릿을 다운로드했습니다.');
    } catch (err) {
      toast.error(friendlyMessage(err, '템플릿 다운로드에 실패했습니다.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleFilePicked = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(
      { file, supplierScope: uploadScope === 'ALL' ? '' : uploadScope },
      {
        onSuccess: (result) => {
          const failed = result.failCount + result.statusChangeFailCount;
          if (failed > 0) {
            toast.error(
              `등록 완료 — 성공 ${result.successCount}건, 실패 ${result.failCount}건, 상태변경 실패 ${result.statusChangeFailCount}건`,
            );
          } else {
            toast.success(`등록 완료 — ${result.successCount}건 모두 성공했습니다.`);
          }
          setDetailJobId(result.jobId);
        },
        onError: (err) => toast.error(friendlyMessage(err, '업로드/등록에 실패했습니다.')),
      },
    );
    // 같은 파일 재업로드 허용을 위해 input 값 초기화.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const columns: DataTableColumn<InvoiceBulkJob>[] = [
    {
      key: 'createdAt',
      header: '등록일',
      width: 150,
      cell: (row) => (
        <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.createdAt}
        </span>
      ),
    },
    {
      key: 'fileName',
      header: '파일명',
      cell: (row) => (
        <span className="block max-w-[260px] truncate text-[13px]" style={{ color: 'var(--content-strong)' }} title={row.fileName}>
          {row.fileName}
        </span>
      ),
    },
    {
      key: 'supplierScope',
      header: '공급사',
      width: 90,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {SUPPLIER_SCOPE_LABELS[row.supplierScope]}
        </span>
      ),
    },
    {
      key: 'counts',
      header: '전체 / 성공 / 실패 / 상태변경실패',
      cell: (row) => (
        <span className="flex items-center gap-2 text-[13px] tabular-nums">
          <span style={{ color: 'var(--content-strong)' }}>{row.totalCount}</span>
          <span style={{ color: 'var(--content-muted)' }}>/</span>
          <span style={{ color: 'var(--status-success-fg)' }}>{row.successCount}</span>
          <span style={{ color: 'var(--content-muted)' }}>/</span>
          <span style={{ color: 'var(--signal-danger)' }}>{row.failCount}</span>
          <span style={{ color: 'var(--content-muted)' }}>/</span>
          <span style={{ color: 'var(--status-warn-fg)' }}>{row.statusChangeFailCount}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: '상태',
      width: 90,
      align: 'center',
      cell: (row) => (
        <StatusBadge label={BULK_JOB_STATUS_LABELS[row.status]} variant={STATUS_VARIANT[row.status]} size="sm" />
      ),
    },
    {
      key: 'detail',
      header: '',
      width: 72,
      align: 'right',
      cell: (row) => (
        <Button variant="outline" size="sm" onClick={() => setDetailJobId(row.jobId)}>
          상세
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title="송장 일괄등록"
      breadcrumb={[{ label: '주문 관리', href: '/orders' }, { label: '송장 일괄등록' }]}
      description="배송대기 주문을 엑셀로 받아 송장번호를 기입하고 업로드하면 배송중/배송완료로 일괄 전환됩니다."
      data-testid="invoice-bulk-page"
    >
      {/* 다운로드 / 업로드 */}
      <SectionCard title="엑셀 다운로드 · 업로드">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
                공급사 범위
              </span>
              <div className="w-[160px]">
                <RadixSelect
                  value={uploadScope}
                  onChange={(v) => setUploadScope(v as SupplierScope)}
                  options={supplierUploadOptions}
                  disabled={!canWrite}
                  aria-label="공급사 범위"
                  data-testid="invoice-upload-scope"
                />
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleDownload()}
              disabled={downloading}
              data-testid="invoice-download"
            >
              <Download size={15} aria-hidden="true" />
              {downloading ? '다운로드 중…' : '엑셀 다운로드'}
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              data-testid="invoice-file-input"
              onChange={(e) => handleFilePicked(e.target.files?.[0])}
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canWrite || upload.isPending}
              data-testid="invoice-upload"
            >
              <Upload size={15} aria-hidden="true" />
              {upload.isPending ? '등록 중…' : '엑셀 업로드 · 등록하기'}
            </Button>
          </div>

          {!canWrite ? (
            <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
              송장 등록 권한이 없어 다운로드만 가능합니다.
            </p>
          ) : null}

          <CourierReference />
        </div>
      </SectionCard>

      {/* 검색 툴바 */}
      <FilterBar
        filters={
          <>
            <FilterField label="공급사">
              <RadixSelect
                value={draft.supplierScope}
                onChange={(v) => setDraft((p) => ({ ...p, supplierScope: v as SupplierScope | '' }))}
                options={supplierFilterOptions}
                aria-label="공급사 필터"
              />
            </FilterField>
            <FilterField label="검색어">
              <Input
                value={draft.keyword}
                onChange={(e) => setDraft((p) => ({ ...p, keyword: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilter();
                }}
                placeholder="파일명 / 주문번호"
              />
            </FilterField>
            <FilterField label="등록상태">
              <RadixSelect
                value={draft.status}
                onChange={(v) => setDraft((p) => ({ ...p, status: v as BulkJobStatus | '' }))}
                options={statusFilterOptions}
                aria-label="등록상태 필터"
              />
            </FilterField>
            <FilterField label="등록일(시작)">
              <Input
                type="date"
                value={draft.dateFrom}
                onChange={(e) => setDraft((p) => ({ ...p, dateFrom: e.target.value }))}
              />
            </FilterField>
            <FilterField label="등록일(종료)">
              <Input
                type="date"
                value={draft.dateTo}
                onChange={(e) => setDraft((p) => ({ ...p, dateTo: e.target.value }))}
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button size="sm" onClick={applyFilter}>
              검색
            </Button>
            <Button size="sm" variant="outline" onClick={resetFilter}>
              초기화
            </Button>
          </>
        }
      />

      {/* 등록 리스트 */}
      <SectionCard title="등록 내역">
        {jobsQuery.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[40px] w-full" />
            ))}
          </div>
        ) : jobsQuery.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>등록 내역을 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={() => void jobsQuery.refetch()}>
              다시 시도
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <DataTable
              columns={columns}
              rows={jobsQuery.data?.items ?? []}
              rowKey={(row) => row.jobId}
              emptyState="등록 내역이 없습니다."
              stickyHeader
              ariaLabel="송장 일괄등록 내역"
            />
            {totalItems > PAGE_SIZE ? (
              <Pagination total={totalItems} currentPage={page} pageSize={PAGE_SIZE} onPageChange={setPage} />
            ) : null}
          </div>
        )}
      </SectionCard>

      <InvoiceBulkJobDetailDialog jobId={detailJobId} onClose={() => setDetailJobId(null)} />
    </PageShell>
  );
};

/** 검색 폼 입력 draft — applied 필터와 동일 형태. */
interface InvoiceBulkFilterDraft {
  supplierScope: SupplierScope | '';
  keyword: string;
  status: BulkJobStatus | '';
  dateFrom: string;
  dateTo: string;
}
